import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import {
  useGetOpeningStocksQuery,
  useDeleteOpeningStockMutation,
} from "../../slices/openingStocksApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice"; // For Pqty and Pcost
import { toast } from "react-toastify";
import { useState } from "react";

const OpeningStockListScreen = () => {
  // State for filtering, pagination, and date range
  const [filter, setFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch opening stocks and supplies data
  const { data: openingStockData, isLoading, error, refetch } =
    useGetOpeningStocksQuery(pageNumber);
  const { data: suppliesData } = useGetSuppliesQuery(); // Supplies data for Pqty and Pcost

  // Mutation for deleting opening stocks
  const [deleteOpeningStock, { isLoading: loadingDelete }] =
    useDeleteOpeningStockMutation();

  // Handle deletion
  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this opening stock?")) {
      try {
        await deleteOpeningStock(id).unwrap();
        toast.success("Opening stock deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete opening stock.");
      }
    }
  };

  // Filter opening stocks based on product name, note, and date range
  const filterOpeningStocks = () => {
    if (!openingStockData?.openingStocks) return [];
    return openingStockData.openingStocks.filter((openingStock) => {
      const openingStockDate = new Date(openingStock.date);
      const isWithinDateRange =
        (!startDate || openingStockDate >= new Date(startDate)) &&
        (!endDate || openingStockDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (openingStock.note?.toLowerCase().includes(filter.toLowerCase()) ||
          openingStock.openingStockItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  // Helper functions for Pqty, Pcost, and AverageCost
  const calculatePqty = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalQty, supply) => {
        const productItems = supply.supplyItems.filter(
          (item) => item.name === productName
        );
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalCost, supply) => {
        const productItems = supply.supplyItems.filter(
          (item) => item.name === productName
        );
        const productCost = productItems.reduce(
          (cost, item) => cost + item.itemCost,
          0
        );
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (itemCost, qty, productName) => {
    const Pcost = calculatePcost(productName);
    const Pqty = calculatePqty(productName);
    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0; // Avoid division by zero
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };


  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Opening Stocks</h1>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={2} className="text-end">
          <Button onClick={() => refetch()}>Reset</Button>
        </Col>
      </Row>

      {/* Totals Display */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>Total Revenue: ${filterOpeningStocks()
            .reduce(
              (total, openingStock) =>
                total +
                openingStock.openingStockItems.reduce(
                  (subTotal, item) => subTotal + item.qty * item.price,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${filterOpeningStocks()
            .reduce(
              (total, openingStock) =>
                total +
                openingStock.openingStockItems.reduce(
                  (subTotal, item) => subTotal + item.itemCost,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${filterOpeningStocks()
            .reduce(
              (total, openingStock) =>
                total +
                openingStock.openingStockItems.reduce(
                  (subTotal, item) => subTotal + item.profit,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Table */}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || "An error occurred"}
        </Message>
      ) : (
        <>
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>PRODUCT NAME</th>
              <th>QTY</th>
              <th>PQTY</th>
              <th>REV</th>
              <th>COST</th>
              <th>PCOST</th>
              <th>AVG COST</th>
              <th>PROFIT</th>
              <th>NOTE</th>
              <th>IMAGE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filterOpeningStocks().map((openingStock) =>
              openingStock.openingStockItems.map((item) => {
                const Pqty = calculatePqty(item.name);
                const Pcost = calculatePcost(item.name);
                const averageCost = calculateAverageCost(
                  item.itemCost,
                  item.qty,
                  item.name
                );

                return (
                  <tr key={`${openingStock._id}-${item.product}`}>
                    <td>{new Date(openingStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{Pqty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>${item.itemCost.toFixed(2)}</td>
                    <td>${Pcost.toFixed(2)}</td>
                    <td>${averageCost.toFixed(2)}</td>
                    <td>${item.profit.toFixed(2)}</td>
                    <td>{openingStock.note || "N/A"}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/openingStock/${openingStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                          as={Link}
                          to={`/admin/openingStock/${openingStock._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                          >
                          Edit
                        </Button>

                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(openingStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
        {/* Pagination */}
          <Pagination>
            {[...Array(openingStockData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default OpeningStockListScreen;